import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, provideZoneChangeDetection } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material/material.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { PortalModule } from './layouts/portal/portal.module';
import { DefaultModule } from './layouts/default/default.module';
import { AbilityModule } from '@casl/angular';
import { Ability, createAliasResolver, PureAbility } from '@casl/ability';
import { EffectsModule } from '@ngrx/effects';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';
import { SharedModule } from './shared/shared.module';
import { ProjectsModule } from './modules/projects/projects.module';
import { SearchModule } from './modules/search/search.module';
import * as Sentry from '@sentry/angular';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { SentryHttpInterceptor } from './shared/services/sentry-http.interceptor';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { httpInterceptorProviders } from './shared/http-interceptors';
import { APP_TOOLTIP_PROVIDER } from './shared/helpers/tooltip-defaults';

export function sessionStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({
    keys: ['sitenotes.shared', 'sitenotes.projects', 'sitenotes.search'],
    rehydrate: true,
    storage: sessionStorage,
  })(reducer);
}

const metaReducers: Array<MetaReducer<any, any>> = [sessionStorageSyncReducer];

const resolveAction = createAliasResolver({
  crud: ['create', 'read', 'update', 'delete'],
});

@NgModule({
  declarations: [AppComponent],
  imports: [
    SharedModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: false }),
    PortalModule,
    DefaultModule,
    AbilityModule,
    StoreModule.forRoot({}, { metaReducers }),
    EffectsModule.forRoot([]),
    ProjectsModule,
    SearchModule,
  ],
  providers: [
    // Tooltips are white chips app-wide (DESIGN.md)
    APP_TOOLTIP_PROVIDER,
    { provide: Ability, useValue: new Ability([], { resolveAction }) },
    { provide: PureAbility, useExisting: Ability },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    { provide: HTTP_INTERCEPTORS, useClass: SentryHttpInterceptor, multi: true },
    httpInterceptorProviders,
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideCharts(withDefaultRegisterables()),
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
}
